<template>
  <PForm
    ref="pForm"
    heading="Auto Generate Invoice"
    sub-heading="Fill and verify the details as per below structure"
    :primary-cta="submitButton"
    @close="closeForm"
    @reset="resetForm"
    @submit="onSubmit"
  >
    <fieldset v-if="!addressFields.dispatch_address_info_present">
      <legend>
        <span>Vendor's Dispatch from Address</span>
      </legend>

      <div class="bg-primary-20 q-pa-md q-mt-sm rounded-borders">
        <span>{{ invoice?.dispatch_address || "" }}</span>
      </div>

      <div class="row q-col-gutter-lg q-mt-none">
        <div class="col-4">
          <PInput
            v-model.number="formData.dispatch_from_address.zip"
            :loading="populatingCityState['dispatch_from_address'].loading"
            :validations="{
              label: 'Pincode',
              length: 6,
              required: true,
              type: 'number',
            }"
            debounce="300"
            label="Dispatch Pincode"
            placeholder="Enter pincode"
            type="number"
            @update:model-value="
              (pincode) =>
                populateCityState({ pincode, type: 'dispatch_from_address' })
            "
          />
        </div>
        <div class="col-4">
          <PInput
            v-model="formData.dispatch_from_address.city"
            label="Dispatch City"
            placeholder="Enter city"
            required
          />
        </div>
        <div class="col-4">
          <PSelectInput
            v-model="formData.dispatch_from_address.state_code"
            :options="unref(stateFilter.entities)"
            label="Dispatch State"
            option-label="name"
            option-value="state_code"
            placeholder="Select state"
            required
            @filter="stateFilter.onFilter"
            @filter-abort="stateFilter.onFilterAbort"
            @update:model-value="
              (stateCode) =>
                populateState({ stateCode, type: 'dispatch_from_address' })
            "
          />
        </div>
        <div class="col-12">
          <PInput
            v-model="formData.dispatch_from_address.address"
            :validations="{
              maxLength: 100,
            }"
            help="Remove state, city, and pincode. Maximum 100 character allowed"
            label="Dispatch Address Line 1"
            placeholder="Enter address line 1"
            type="textarea"
            counter
            required
          />
        </div>
      </div>
    </fieldset>
    <fieldset v-if="!addressFields.shipping_address_info_present">
      <legend>Customer's Shipping Address</legend>

      <div class="bg-primary-20 q-pa-md q-mt-sm rounded-borders">
        <span>{{ invoice?.shipping_address_info?.shop_name }}</span
        ><br />
        <span>{{ invoice?.shipping_address }}</span>
      </div>

      <div class="row q-col-gutter-lg q-mt-none">
        <div class="col-4">
          <PInput
            v-model.number="formData.shipping_address.zip"
            :loading="unref(populatingCityState)['shipping_address'].loading"
            :validations="{
              label: 'Pincode',
              length: 6,
              required: true,
              type: 'number',
            }"
            debounce="300"
            label="Shipping Pincode"
            placeholder="Enter pincode"
            type="number"
            @update:model-value="
              (pincode) =>
                populateCityState({ pincode, type: 'shipping_address' })
            "
          />
        </div>
        <div class="col-4">
          <PInput
            v-model="formData.shipping_address.city"
            label="Shipping City"
            placeholder="Enter city"
            required
          />
        </div>
        <div class="col-4">
          <PSelectInput
            v-model="formData.shipping_address.state_code"
            :options="unref(stateFilter.entities)"
            label="Shipping State"
            option-label="name"
            option-value="state_code"
            placeholder="Select state"
            required
            @filter="stateFilter.onFilter"
            @filter-abort="stateFilter.onFilterAbort"
            @update:model-value="
              (stateCode) =>
                populateState({ stateCode, type: 'shipping_address' })
            "
          />
        </div>
        <div class="col-12">
          <PInput
            v-model="formData.shipping_address.address"
            :validations="{
              maxLength: 100,
            }"
            help="Remove state, city, and pincode. Maximum 100 character allowed"
            label="Shipping Address Line 1"
            placeholder="Enter address line 1"
            type="textarea"
            counter
            required
          />
        </div>
      </div>
    </fieldset>
    <fieldset v-if="!addressFields.billing_address_info_present">
      <legend>Customer's Billing Address</legend>

      <div class="bg-primary-20 q-pa-md q-mt-sm rounded-borders">
        <span>{{ invoice?.billing_address_info?.shop_name }}</span
        ><br />
        <span>{{ invoice?.billing_address }}</span>
      </div>

      <div class="row q-col-gutter-lg q-mt-none">
        <div class="col-4">
          <PInput
            v-model.number="formData.billing_address.zip"
            :loading="populatingCityState['billing_address'].loading"
            :validations="{
              label: 'Pincode',
              length: 6,
              required: true,
              type: 'number',
            }"
            debounce="300"
            label="Billing Pincode"
            placeholder="Enter pincode"
            type="number"
            @update:model-value="
              (pincode) =>
                populateCityState({ pincode, type: 'billing_address' })
            "
          />
        </div>
        <div class="col-4">
          <PInput
            v-model="formData.billing_address.city"
            label="Billing City"
            placeholder="Enter city"
            required
          />
        </div>
        <div class="col-4">
          <PSelectInput
            v-model="formData.billing_address.state_code"
            :options="unref(stateFilter.entities)"
            label="Billing State"
            option-label="name"
            option-value="state_code"
            placeholder="Select state"
            required
            @filter="stateFilter.onFilter"
            @filter-abort="stateFilter.onFilterAbort"
            @update:model-value="
              (stateCode) =>
                populateState({ stateCode, type: 'billing_address' })
            "
          />
        </div>
        <div class="col-12">
          <PInput
            v-model="formData.billing_address.address"
            :validations="{
              maxLength: 100,
            }"
            help="Remove state, city, and pincode. Maximum 100 character allowed"
            label="Billing Address Line 1"
            placeholder="Enter address line 1"
            type="textarea"
            counter
            required
          />
        </div>
      </div>
    </fieldset>
    <fieldset>
      <legend>Transaction Settings</legend>

      <div class="row q-col-gutter-lg q-mt-lg">
        <div class="col-6">
          <PSelectInput
            v-model="formData.billing_branch"
            :options="unref(billingBranchFilter.entities)"
            option-label="branch_name"
            label="Poshn's Billing Branch"
            placeholder="Select an option"
            required
            @filter="billingBranchFilter.onFilter"
            @filter-abort="billingBranchFilter.onFilterAbort"
          />
        </div>
        <div class="col-6">
          <PSelectInput
            v-model="formData.transaction_series_name"
            :options="unref(transactionSeriesFilter.entities)"
            option-label="name"
            option-value="name"
            label="Poshn's Transaction Series Name"
            placeholder="Select an option"
            required
            @filter="transactionSeriesFilter.onFilter"
            @filter-abort="transactionSeriesFilter.onFilterAbort"
          />
        </div>
        <div class="col-6">
          <PSelectInput
            v-model="formData.fssai"
            :options="unref(fssaiNumberFilter.entities)"
            option-label="fssai"
            option-value="fssai"
            label="Poshn's FSSAI Number"
            placeholder="Select an option"
            required
            @filter="fssaiNumberFilter.onFilter"
            @filter-abort="fssaiNumberFilter.onFilterAbort"
          >
            <template #option="{ itemProps, opt }">
              <q-item v-bind="itemProps">
                <q-item-section>
                  <q-item-label class="text-sm">
                    <span class="text-neutral">{{ opt.fssai }}</span>
                  </q-item-label>
                  <q-item-label caption lines="1">
                    <span class="text-neutral-70 block">
                      {{ opt.branch }}
                    </span>
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </PSelectInput>
        </div>
      </div>
    </fieldset>
    <fieldset>
      <legend>Tick Applicable Options</legend>

      <PField v-model="formData.autoptions" class="q-mt-lg">
        <template #control>
          <POptionGroup
            v-model="formData.autoptions"
            :dense="false"
            :options="[
              {
                description: 'Push to IRP',
                label: 'Generate E-Invoice',
                value: '0',
              },
              {
                description: 'Vendor\'s bill upload to zoho',
                label: 'Push Purchase Bill',
                value: '2',
              },
              {
                description: 'Only if request is found',
                label: 'Generate E-Way Bill',
                value: '1',
              },
            ]"
            class="flex"
            type="checkbox"
            :style="{ gap: '1.25rem' }"
          />
        </template>
      </PField>
    </fieldset>
  </PForm>
</template>

<script setup>
import { useQuasar } from "quasar";

import invoiceAutoGenFormModel from "src/modules/accounts/models/invoiceAutoGenForm";

import { getPincodeDetails } from "src/api/address";
import PConfirmDialog from "src/components/PConfirmDialog.vue";
import { useStateFilter } from "src/composables/stateFilter";

import { billingBranches, fssaiNumbers, transactionSeries } from "assets/data";
import {
  extractPincodeFromAddress,
  removeGstinFromAddress,
} from "src/assets/scripts/utilities/helpers";
import { nextTick, unref } from "vue";
import { CancelToken } from "assets/scripts/utilities/api-clients";

const $q = useQuasar();

const { states, stateFilter } = useStateFilter();

const pForm = ref(null);

const { autoGenFormState, invoice } = storeToRefs(useInvoiceStore());
const { autoGenerateInvoice, toggleInvoiceAutoGenForm } = useInvoiceStore();

const { formData, closeForm, resetForm } = useForm({
  formModel: invoiceAutoGenFormModel({
    _id: unref(invoice)?._id,
  }),
  formRef: pForm,
  isForm: autoGenFormState,
  toggleForm: toggleInvoiceAutoGenForm,
});

const populatingCityState = ref({
  dispatch_from_address: {
    loading: false,
    source: null,
  },
  shipping_address: {
    loading: false,
    source: null,
  },
  billing_address: {
    loading: false,
    source: null,
  },
});

const populateCityState = async ({ pincode, type }) => {
  try {
    if (pincode && pincode.toString().length === 6) {
      unref(populatingCityState)[type].loading = true;

      unref(populatingCityState)[type].source?.cancel("Request cancelled");

      unref(populatingCityState)[type].source = CancelToken.source();

      const response = await getPincodeDetails(pincode, {
        cancelToken: unref(populatingCityState)[type].source?.token,
      });

      unref(formData)[type].city = response?.data?.[0]?.PostOffice?.[0]?.Block;

      unref(formData)[type].state = response?.data?.[0]?.PostOffice?.[0]?.State;

      unref(formData)[type].state_code = unref(states).find(
        (state) => state.name === unref(formData)[type].state,
      )?.state_code;
    }
  } catch (error) {
    console.error(error);
  } finally {
    unref(populatingCityState)[type].loading = false;
  }
};

const populatePincode = () => {
  try {
    unref(formData).dispatch_from_address.zip = extractPincodeFromAddress(
      unref(formData)?.dispatch_from_address?.address,
    );

    unref(formData).shipping_address.zip = extractPincodeFromAddress(
      unref(formData)?.shipping_address?.address,
    );

    unref(formData).billing_address.zip = extractPincodeFromAddress(
      unref(formData)?.billing_address?.address,
    );

    populateCityState({
      pincode: unref(formData)?.dispatch_from_address?.zip,
      type: "dispatch_from_address",
    });

    populateCityState({
      pincode: unref(formData)?.shipping_address?.zip,
      type: "shipping_address",
    });

    populateCityState({
      pincode: unref(formData)?.billing_address?.zip,
      type: "billing_address",
    });
  } catch (error) {
    console.error(error);
  }
};

const populateState = ({ stateCode, type }) => {
  const selectedState = unref(states)?.find((state) => {
    return state.state_code === stateCode;
  });

  unref(formData)[type].state = selectedState?.name || "";
};

const setAddressDetails = (formAddress, addressInfo) => {
  formAddress.zip = addressInfo.pincode;

  formAddress.city = addressInfo.city;

  formAddress.state_code = addressInfo.state_code;

  formAddress.state = addressInfo.state;
};

const addressFields = computed(() => {
  return {
    billing_address_info_present: areAddressFieldsPresent(
      unref(invoice)?.billing_address_info,
    ),
    dispatch_address_info_present: areAddressFieldsPresent(
      unref(invoice)?.dispatch_address_info,
    ),
    shipping_address_info_present: areAddressFieldsPresent(
      unref(invoice)?.shipping_address_info,
    ),
  };
});

const areAddressFieldsPresent = (key) => {
  return Boolean(
    key?.address_line &&
      key?.city &&
      key?.pincode &&
      key?.state_code &&
      key?.state,
  );
};

watch(
  () => unref(autoGenFormState).open,
  (isOpen) => {
    if (isOpen) {
      nextTick(() => {
        // Updates addresses and info
        unref(formData).billing_address.address =
          removeGstinFromAddress(
            unref(invoice)?.billing_address_info?.address_line ||
              unref(invoice)?.billing_address ||
              "",
          ) || "";

        unref(formData).dispatch_from_address.address =
          removeGstinFromAddress(
            unref(invoice)?.dispatch_address_info?.address_line ||
              unref(invoice)?.dispatch_address ||
              "",
          ) || "";

        unref(formData).shipping_address.address = removeGstinFromAddress(
          unref(invoice)?.shipping_address_info?.address_line ||
            unref(invoice)?.shipping_address ||
            "",
        );

        const {
          billing_address_info,
          shipping_address_info,
          dispatch_address_info,
        } = unref(invoice);

        if (
          billing_address_info &&
          shipping_address_info &&
          dispatch_address_info
        ) {
          setAddressDetails(
            unref(formData).billing_address,
            billing_address_info,
          );

          setAddressDetails(
            unref(formData).shipping_address,
            shipping_address_info,
          );

          setAddressDetails(
            unref(formData).dispatch_from_address,
            dispatch_address_info,
          );

          // Sets billing branch
          const poshnBillingBranch =
            billingBranches.find(
              (branch) =>
                branch.address.state_code === billing_address_info.state_code,
            ) ??
            billingBranches.find(
              (branch) => branch.address.state_code === "DL",
            );

          unref(formData).billing_branch = poshnBillingBranch;

          unref(formData).branch_state_code =
            poshnBillingBranch?.address?.state_code;
        } else {
          populatePincode();
        }

        // Sets Invoice ID
        unref(formData)._id = unref(invoice)?._id;
      });
    }
  },
  {
    immediate: true,
  },
);

const billingBranchFilter = useFilter({
  entities: billingBranches,
  props: ["branch_name"],
});

const fssaiNumberFilter = useFilter({
  entities: fssaiNumbers,
  props: ["fssai"],
});

const transactionSeriesForSelectedBranch = computed(() => {
  unref(formData).transaction_series_name = "";

  return transactionSeries.filter((series) =>
    series.associated_branches.some((associatedBranch) => {
      return (
        associatedBranch.branch_name ===
        unref(formData)?.billing_branch?.branch_name
      );
    }),
  );
});

const transactionSeriesFilter = ref({});

watchEffect(() => {
  transactionSeriesFilter.value = useFilter({
    entities: unref(transactionSeriesForSelectedBranch),
    props: ["name"],
  });
});

watch(
  () => unref(formData).billing_branch,
  (billingBranch) => {
    unref(formData).branch_state_code =
      billingBranch?.address?.state_code || "";

    let branchFssai = fssaiNumbers.find(
      (fssai) => fssai.branch === billingBranch?.branch_name,
    )?.fssai;

    if (branchFssai) {
      unref(formData).fssai = branchFssai;
    }
  },
);

let isAutoGenerating = ref(false);

const isGstExempted = computed(() => {
  return (
    !unref(invoice).total_tax &&
    unref(invoice)?.lineitems?.findIndex(
      (line_item) => !line_item.gst_exempted,
    ) === -1
  );
});

const onConfirmAutoGeneration = async () => {
  try {
    isAutoGenerating.value = true;

    await autoGenerateInvoice({ invoice: unref(formData), isGstExempted });

    closeForm(true);
  } catch (error) {
    console.error(error);
  } finally {
    isAutoGenerating.value = false;
  }
};

const confirmAutoGeneration = () => {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      labelClass: "text-center q-ma-xl rounded-borders",
      text: "Are you sure want to proceed?",
      onConfirm: onConfirmAutoGeneration,
    },
  });
};

const onSubmit = async () => {
  confirmAutoGeneration();
};

const submitButton = computed(() => {
  return {
    loading: unref(isAutoGenerating),
  };
});
</script>
